import { IdentityApi, OAuthApi } from '@backstage/core-plugin-api';
import fetch from 'node-fetch';

/**
 * Options for configuring requests made by the ADO middleware.
 */
export interface AdoMiddlewareOptions extends fetch.RequestInit {}

/**
 * Interface for ADO request middleware, defining the method for making authorized requests.
 */
export interface IADORequestMiddleware {
  /**
   * Makes an authorized request to the specified URL.
   * @template T - The type of the response data.
   * @param url - The URL to send the request to.
   * @param options - Additional options for the request.
   * @returns A promise that resolves to the response data of type `T`.
   */
  request<T>(
    url: fetch.RequestInfo,
    options?: AdoMiddlewareOptions,
  ): Promise<T>;
}

/**
 * Middleware class for handling authorized requests to Azure DevOps (ADO) services.
 * @implements IADORequestMiddleware
 *
 * @module ADORequestMiddleware
 */
export class ADORequestMiddleware implements IADORequestMiddleware {
  private readonly identityApi: IdentityApi;
  private readonly oAuthApi: OAuthApi;
  private readonly adoApiId: string;

  /**
   * Creates an instance of ADORequestMiddleware.
   * @param options - The options required to initialize the middleware.
   * @param options.identityApi - The IdentityApi instance for retrieving user credentials.
   * @param options.authApi - The OAuthApi instance for acquiring ADO access tokens.
   * @param options.adoApiId - The ID of the ADO API being accessed.
   */
  constructor(options: {
    identityApi: IdentityApi;
    authApi: OAuthApi;
    adoApiId: string;
  }) {
    this.identityApi = options.identityApi;
    this.oAuthApi = options.authApi;
    this.adoApiId = options.adoApiId;
  }

  /**
   * Retrieves the ADO access token header.
   * @returns A promise resolving to an object containing the token header.
   * @throws An error if the token cannot be retrieved.
   */
  private getAdoAccessTokenHeader = async () => {
    const token = await this.getAdoAccessToken();
    return { 'x-ado-token': token };
  };

  /**
   * Retrieves the ADO access token using the OAuth API.
   * @returns A promise resolving to the ADO access token string.
   * @throws An error if the token cannot be retrieved.
   */
  private getAdoAccessToken = async () => {
    try {
      const token = await this.oAuthApi.getAccessToken(
        `${this.adoApiId}/vso.project ${this.adoApiId}/vso.profile`,
      );
      return token;
    } catch (error) {
      return '';
    }
  };

  /**
   * Authorizes a request with the necessary headers.
   * @returns A function that makes an authorized request to the specified URL.
   * @throws An error if authorization is not possible due to missing credentials or tokens.
   */
  private async authorizeRequest() {
    const identity = await this.identityApi.getCredentials();
    const token = identity?.token;
    const isAuthorized = !!token;

    const requestHeaders = await this.getAdoAccessTokenHeader();

    return async (url: fetch.RequestInfo, options?: fetch.RequestInit) => {
      try {
        if (!isAuthorized) {
          throw new Error('No authentication token available');
        }

        if (!requestHeaders) {
          throw new Error('No ADO access token available');
        }

        const response = await fetch(url, {
          ...options,
          method: options?.method || 'GET',
          headers: {
            ...options?.headers,
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...requestHeaders,
          },
        });

        if (!response.ok) {
          const responseBody = await response.json();
          console.error('Error fetching data:', responseBody);
          const errMsg =
            responseBody?.error?.message ??
            `HTTP error! Status: ${response.status}`;
          throw new Error(errMsg);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
    };
  }

  /**
   * Makes an authorized request to the specified URL.
   * @template T - The type of the response data.
   * @param url - The URL to send the request to.
   * @param options - Additional options for the request.
   * @returns A promise that resolves to the response data of type `T`.
   */
  async request<T>(
    url: fetch.RequestInfo,
    options?: AdoMiddlewareOptions,
  ): Promise<T> {
    const authorizedRequest = await this.authorizeRequest();
    return await authorizedRequest(url, options);
  }
}
